import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import { getHasSavedResults } from 'ctm-selectors/selectors/results';
import { convertStringCase } from 'ctm-components';
import { displayBooleanString, onReplaceLinkClick } from 'ctm-util-functions';
import { EditMyDetailsSection } from './edit_my_details_section';
import {reporterLog} from "ctm-app/client/sagas/reporter";

const BASE_URL = '/car-insurance/journey';

/*
 * EditMyDetails includes Edit My Details Links for the Sidebar Accordion in Results
 * @return {Node} EditMyDetails returns EditMyDetails component
 */
class _EditMyDetails extends PureComponent {

    /**
     * Get finance label
     * @param {String} value finance
     * @return {String} finance Returns finance label
     */
    getFinanceLabel = (value) => ({
        FINANCE_HIRE_PURCHASE: 'Yes, Finance / Hire Purchase',
        LEASE: 'Yes, Lease',
        NONE: 'No Finance'
    }[value]);

    quoteModalOnClick = (path) => {
        const { dispatch } = this.props;
        const hasSavedResults = getHasSavedResults();

        if (hasSavedResults) {
            dispatch({
                type: 'SET_RESULTS_CONFIG_VALUE',
                key: 'helpers.updateQuoteModal',
                value: { open: true }
            });
            dispatch({
                type: 'SET_RESULTS_CONFIG_VALUE',
                key: 'helpers.quoteModalRedirect',
                value: `${BASE_URL}/${path}`
            });
        }
    };

    /**
     * Get useType label
     * @param {String} value useType
     * @return {String} useType Returns useType label
     */
    getUsageTypeLabel = (value) => ({
        PRIVATE_AND_COMMUTING_TO_WORK: 'Private and/or commuting to work only',
        PRIVATE_OCCASIONAL_BUSINESS: 'Private and Occasional Business',
        PRIVATE_BUSINESS: 'Private and Business',
        BUSINESS_ONLY: 'Business Only'
    }[value]);

    /**
     * Get previousInsurer label
     * @param {String} value previousInsurer
     * @return {String} previousInsurer Returns previousInsurer label
     */
    getPreviousInsurerLabel = (value) => ({
        FIRST_FOR_WOMEN: '1st for Women',
        AAMI: 'AAMI',
        AAI_LIMITED: 'AAI Limited',
        ALLIANZ: 'Allianz',
        ANZ: 'ANZ',
        APIA: 'APIA',
        AUTO_GENERAL: 'Auto & General',
        AUSSIE: 'Aussie',
        AUS_POST: 'Australia Post Insurance',
        BOQ_INSURANCE: 'Bank of Queensland Insurance',
        BENDIGO_BANK: 'Bendigo Bank',
        BINGLE: 'Bingle',
        BUDGET_DIRECT: 'Budget Direct',
        BUPA_INSURANCE: 'BUPA Insurance',
        CARPEESH: 'Carpeesh',
        CGU: 'CGU',
        COLES_INSURANCE: 'Coles Insurance',
        COMMINSURE: 'Comminsure',
        ELDERS_INSURANCE: 'Elders Insurance',
        ERIC_INSURANCE: 'Eric Insurance',
        GIO: 'GIO',
        HBF_INSURANCE: 'HBF Insurance',
        HOLLARD: 'Hollard',
        HUDDLE: 'Huddle',
        ING: 'ING',
        JUST_CAR_INSURANCE: 'Just Car Insurance',
        KOBA: 'Koba',
        LUMLEY: 'Lumley',
        NAB: 'NAB',
        NRMA: 'NRMA',
        OCEANIA_INSURANCE: 'Oceania Insurance',
        OZICARE: 'Ozicare',
        PD_INSURANC: 'PD Insurance',
        QANTAS_INSURANCE: 'Qantas Insurance',
        QBE: 'QBE',
        RAA: 'RAA',
        RAC: 'RAC',
        RACQ: 'RACQ',
        RACT: 'RACT',
        RACV: 'RACV',
        REAL_INSURANCE: 'Real Insurance',
        RETIREASE: 'Retirease',
        SGIC: 'SGIC',
        SGIO: 'SGIO',
        SHANNONS: 'Shannons',
        STELLA: 'Stella',
        ST_GEORGE_BANK: 'St George Bank',
        SUNCORP: 'Suncorp',
        SWANN: 'Swann',
        VERO: 'Vero',
        VIRGIN_MONEY_INSURANCE: 'Virgin Money Insurance',
        WFI: 'WFI',
        WESTPAC: 'Westpac',
        WOOLWORTHS: 'Everyday Insurance',
        YOUI: 'YOUI',
        ZURICH: 'Zurich',
        OTHER: 'Other/Not Listed'
    }[value]);

    /**
     * Get security features item
     * @return {String} security Returns security features item
     */
    getSecurityFeatures = () => {
        const { securityFeatures } = this.props;
        return securityFeatures ? `Security: ${this.getSecurityLabel(securityFeatures)}` : null;
    };

    /**
     * Get security label
     * @param {String} value security
     * @return {String} security Returns security label
     */
    getSecurityLabel = (value) => ({
        NONE: 'No security features',
        ALARM: 'Alarm',
        IMMOBILISER: 'Immobiliser',
        BOTH: 'Alarm & Immobiliser'
    }[value]);

    /**
     * Get licence type label
     * @param {String} value licenceType
     * @return {String} licenceType Returns licenceType label
     */
    getLicenceTypeLabel = (value) => ({
        FULL: 'Full / Open Licence',
        PROVISIONAL2: 'Provisional 2 (Green)',
        PROVISIONAL1: 'Provisional 1 (Red)',
        LEARNERS: 'Learners',
        FOREIGN: 'Foreign Licence'
    }[value]);

    /**
     * Get parkingType label
     * @param {String} value parkingType
     * @return {String} parkingType Returns parkingType label
     */
    getParkingTypeLabel = (value) => ({
        GARAGED: 'Garaged',
        CAR_PARK: 'Car Park',
        STREET: 'Street',
        PARKING_LOT: 'Parking Lot',
        DRIVEWAY: 'Driveway',
        LOCKED_COMPOUND: 'Locked Compound',
        ON_PRIVATE_PROPERTY: 'On Private Property',
        CAR_PORT: 'Carport'
    }[value]);

    /**
     * Get restrictions label
     * @param {String} value rating
     * @return {String} restrictions Returns restrictions label
     */
    getRestrictionsLabel = (value) => ({
        DRIVERS_21_AND_OVER: 'Yes, all drivers will be 21 & over',
        DRIVERS_25_AND_OVER: 'Yes, all drivers will be 25 & over',
        DRIVERS_30_AND_OVER: 'Yes, all drivers will be 30 & over',
        DRIVERS_40_AND_OVER: 'Yes, all drivers will be 40 & over',
        DRIVERS_50_AND_OVER: 'Yes, all drivers will be 50 & over',
        NO_RESTRICTION: 'No restrictions'
    }[value]);

    /**
     * Get employment label
     * @param {String} value employment
     * @return {String} employment Returns employment label
     */
    getEmploymentLabel = (value) => ({
        EMPLOYED_FULL_TIME: 'Employed full-time',
        EMPLOYED_PART_TIME: 'Employed part-time',
        SELF_EMPLOYED: 'Self employed',
        RETIRED: 'Retired',
        STUDENT: 'Student',
        UNEMPLOYED: 'Unemployed',
        HOUSE_WIFE_OR_HUSBAND: 'Home duties'
    }[value]);

    getClaimsLabel = (value) => ({
        0: 'None',
        1: '1 claim',
        2: '2 claims',
        3: '3 claims',
        4: '4 claims',
        5: '5+ claims'
    }[value]);

    /**
     * Get Policy Holder EditMyDetailsSection
     * @return {Node} EditMyDetailsSection
     */
    getAddress = () => {
        const { overnightParkingPostcode, overnightParkingType, hasSavedResults } = this.props;
        const items = [`Postcode: ${overnightParkingPostcode}`, this.getParkingTypeLabel(overnightParkingType)];

        const onClick = () => {
            this.quoteModalOnClick('car_address');
        };

        return (
            <EditMyDetailsSection
                label="Address"
                href={ hasSavedResults ? 'javascript:void();' : `${BASE_URL}/car_address`}
                items={items}
                onClick={onClick} />
        );
    };

    /**
     * Get Car Details EditMyDetailsSection
     * @return {Node} EditMyDetailsSection
     */
    getCarDetails = () => {
        const { series, make, year, model, body, transmission, badge, fuel, colour, description, splitTrafficId, hasSavedResults } = this.props;
        let updatedBadge = badge;
        let updatedDescription = description;

        if (series?.descriptions) {
            updatedBadge = '';
            const keys = ['SERIES', 'BADGE', 'ENGINE', 'TRANSMISSION', 'POWER', 'DRIVE', 'KERBMASS', 'RELEASE'];
            updatedDescription = keys.reduce((prev, curr) => {
                let desc = '';
                if (series.descriptions[curr]?.description !== undefined && series.descriptions[curr]?.description !== null) {
                    desc = series.descriptions[curr].description;
                    if (prev) {
                        desc = ` | ${desc}`;
                    }
                    if (curr === 'RELEASE') {
                        desc = `${desc} Release`;
                    }
                }
                return prev + desc;
            }, '');
        }
        const items = [make, year, model, body, transmission, updatedBadge, fuel, updatedDescription, colour];

        const onClick = () => {
            this.quoteModalOnClick('car_make');
            const { dispatch } = this.props;
            // Set isEditingDetails to true when edit is clicked
            dispatch({
                type: 'SET_VALUE',
                value: true,
                stateKey: 'vertical.quote',
                identifier: 'helpers.prefill.isEditingDetails'
            });
        };
        return (
            <EditMyDetailsSection
                label="Car Details"
                href={hasSavedResults ? 'javascript:void();' : `${BASE_URL}/car_make`}
                items={items}
                onClick={onClick} />
        );
    };

    /**
     * Get Accessories EditMyDetailsSection
     * @return {Node} EditMyDetailsSection
     */
    getAccessories = () => {
        const { factoryOptions, nonStandardAccessories, modified, dispatch, hasSavedResults } = this.props;
        const hasNonStandardAccessories = !!nonStandardAccessories && nonStandardAccessories.size > 0;
        const hasFactoryOptions = !!factoryOptions && factoryOptions.size > 0;
        const items = [
            `Factory options: ${displayBooleanString(hasFactoryOptions)}`,
            `Non-standard accessories: ${displayBooleanString(hasNonStandardAccessories)}`,
            `Modified: ${displayBooleanString(modified)}`
        ];

        // Set isEditingAccessories which is used to determine whether to autoProgressToResults once
        // user gets to the modifications page from accessories.
        const onClick = (event) => {
            if (hasSavedResults) {
                this.quoteModalOnClick('factory_options');
            } else {
                dispatch({
                    type: 'SET_VALUE',
                    identifier: 'helpers.isEditingAccessories',
                    value: true,
                    stateKey: 'vertical.quote'
                });
                onReplaceLinkClick(event, '/car-insurance/journey/factory_options', null);
            }
        };

        return <EditMyDetailsSection label="Accessories" href={hasSavedResults ? 'javascript:void();' : '#'} items={items} onClick={onClick} />;
    };

    /**
     * Get Cover Details EditMyDetailsSection
     * @return {Node} EditMyDetailsSection
     */
    getCoverDetails = () => {
        const { damaged, finance, annualKilometres, useType, currentlyInsured, previousInsurer, hasSavedResults } = this.props;
        const items = [
            `Damage: ${displayBooleanString(damaged)}`,
            this.getSecurityFeatures(),
            this.getFinanceLabel(finance),
            `Approx ${parseFloat(annualKilometres).toLocaleString()} KM's p/year`,
            this.getUsageTypeLabel(useType),
            `Currently Insured : ${displayBooleanString(currentlyInsured)}`
        ];

        if (currentlyInsured === 'Y') {
            items.push(`Previous Insurer : ${this.getPreviousInsurerLabel(previousInsurer)}`);
        }

        const onClick = () => {
            this.quoteModalOnClick('accident_hail_damage');
        };

        return (
            <EditMyDetailsSection
                label="Cover Details"
                href={hasSavedResults ? 'javascript:void();' : `${BASE_URL}/accident_hail_damage`}
                items={items}
                onClick={onClick}
            />
        );
    };

    /**
     * Get Regular Driver EditMyDetailsSection
     * @return {Node} EditMyDetailsSection
     */
    getRegularDriver = () => {
        const {
            driverGender,
            driverDob,
            driverEmployment,
            driverAge,
            driverLicenceType,
            comprehensiveInsured,
            comprehensiveInsuredYears,
            driverPreviousClaims,
            driverOwnsAnotherCar,
            driverOwnsAnotherHome,
            driverPreviousClaimsCount,
            hasSavedResults
        } = this.props;
        let items = [];
        let previousClaimsLabel = '';
        if (driverPreviousClaims) {
            previousClaimsLabel = displayBooleanString(driverPreviousClaims);
        }

        if (driverPreviousClaimsCount) {
            previousClaimsLabel = this.getClaimsLabel(driverPreviousClaimsCount);
        }

        try {
            const licenceTypeLabel = this.getLicenceTypeLabel(driverLicenceType) ?? '';
            items.push(`Gender: ${convertStringCase(driverGender.toLowerCase(), 'title')}`);
            items.push(`D.O.B: ${moment(driverDob).format('DD/MM/YYYY')}`);
            items.push(this.getEmploymentLabel(driverEmployment));
            items.push(`Licence type: ${licenceTypeLabel}`);
            items.push(`Licence age: ${driverAge}`);
            if (comprehensiveInsured) {
                items.push(`Past comprehensive car insurance: ${displayBooleanString(comprehensiveInsured)}`);
            }
            if (comprehensiveInsuredYears) {
                items.push(`Years of comprehensive car insurance: ${comprehensiveInsuredYears}`);
            }
            items.push(`Previous Claims: ${previousClaimsLabel}`);
            items.push(`Owns Another Car: ${displayBooleanString(driverOwnsAnotherCar)}`);
            items.push(`Owns A Home: ${displayBooleanString(driverOwnsAnotherHome)}`);
        } catch (e) {
            items = ['None'];
            reporterLog('Error when trying to getRegularDriver details', e);
        }

        const onClick = () => {
            this.quoteModalOnClick('regular_driver_gender');
        };

        return (
            <EditMyDetailsSection
                label="Regular Driver"
                href={hasSavedResults ? 'javascript:void();' : `${BASE_URL}/regular_driver_gender`}
                items={items}
                onClick={onClick} />
        );
    };

    /**
     * Get Younger Driver EditMyDetailsSection
     * @return {Node} EditMyDetailsSection
     */
    getYoungerDriver = () => {
        const { hasYoungerDriver,
            youngerDriverGender,
            youngerDriverLicenceType,
            youngerDriverDob,
            youngerDriverLicenceAge,
            hasSavedResults } = this.props;
        let items = [];
        if (hasYoungerDriver === 'Y') {
            const licenceTypeLabel = this.getLicenceTypeLabel(youngerDriverLicenceType) ?? '';
            items = [
                `Gender: ${convertStringCase(youngerDriverGender.toLowerCase(), 'title')}`,
                `D.O.B: ${moment(youngerDriverDob).format('DD/MM/YYYY')}`,
                `Licence type: ${licenceTypeLabel}`,
                `Licence age: ${youngerDriverLicenceAge}`
            ];
        } else {
            items = ['None'];
        }

        const onClick = (event) => {
            hasSavedResults ? this.quoteModalOnClick('youngest_driver')
                : onReplaceLinkClick(event, `${BASE_URL}/youngest_driver`, null);
        };

        return (
            <EditMyDetailsSection
                label="Younger Driver"
                href={hasSavedResults ? 'javascript:void();' : 'javascript:;'}
                items={items}
                onClick={onClick} />
        );
    };

    /**
     * Checks if younger than 21
     * @param {string} dob - Date of birth
     * @returns {boolean} returns whether or not the user is over 21
     */
    isYoungerThan21 = (dob) => moment().tz('Australia/Brisbane').diff(dob, 'years', true) < 21;

    /**
     * Get Driver Restrictions EditMyDetailsSection
     * @returns {Node} EditMyDetailsSection
     */
    getDriverRestrictions = () => {
        const { driverOption, driverDob, youngerDriverDob, hasSavedResults } = this.props;
        if (this.isYoungerThan21(driverDob) || this.isYoungerThan21(youngerDriverDob)) {
            return null;
        }
        const items = [`Driver Restrictions: ${this.getRestrictionsLabel(driverOption)}`];

        const onClick = () => {
            this.quoteModalOnClick('driver_option');
        };

        return (
            <EditMyDetailsSection label="Driver Restrictions"
                href={hasSavedResults ? 'javascript:void();' : `${BASE_URL}/driver_option`}
                items={items}
                onClick={onClick} />
        );
    };

    /**
     * Get Contact Details EditMyDetailsSection
     * @return {Node} EditMyDetailsSection
     */
    getContactDetails = () => {
        const { email, mobile, phone, firstName, lastName, hasSavedResults } = this.props;

        const nameLabel = `${firstName} ${lastName}`;
        const items = [nameLabel, email ? `Email: ${email}` : null, mobile || phone ? `Contact: ${mobile || phone}` : null];

        const onClick = () => {
            this.quoteModalOnClick('applicant_details');
        };

        return (
            <EditMyDetailsSection
                label="Contact Details"
                href={hasSavedResults ? 'javascript:void();' : `${BASE_URL}/applicant_details`}
                items={items}
                onClick={onClick} />
        );
    };

    /**
     * Get Commencement Date EditMyDetailsSection
     * @return {Node} EditMyDetailsSection
     */
    getCommencementDate = () => {
        const { commencementDate, hasSavedResults } = this.props;
        const dateLabel = moment(commencementDate).format('DD/MM/YYYY');

        const onClick = () => {
            this.quoteModalOnClick('commencement_date');
        };

        return (
            <EditMyDetailsSection
                label="Commencement Date"
                href={hasSavedResults ? 'javascript:void();' : `${BASE_URL}/commencement_date`}
                items={[dateLabel]}
                onClick={onClick} />
        );
    };

    /**
     * Render the EditMyDetails component
     * @return {Node} EditMyDetails component
     */
    render = () => (
        <div>
            {this.getCarDetails()}
            {this.getAccessories()}
            {this.getCoverDetails()}
            {this.getAddress()}
            {this.getRegularDriver()}
            {this.getYoungerDriver()}
            {this.getDriverRestrictions()}
            {this.getContactDetails()}
            {this.getCommencementDate()}
        </div>
    );

}

_EditMyDetails.propTypes = {
    dispatch: PropTypes.func.isRequired,
    series: PropTypes.object,
    make: PropTypes.string,
    year: PropTypes.string,
    model: PropTypes.string,
    body: PropTypes.string,
    transmission: PropTypes.string,
    badge: PropTypes.string,
    fuel: PropTypes.string,
    colour: PropTypes.string,
    description: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    mobile: PropTypes.string,
    phone: PropTypes.string,
    commencementDate: PropTypes.string,
    overnightParkingPostcode: PropTypes.string,
    overnightParkingType: PropTypes.string,
    hasYoungerDriver: PropTypes.string,
    youngerDriverLicenceType: PropTypes.string,
    youngerDriverGender: PropTypes.string,
    youngerDriverDob: PropTypes.string,
    youngerDriverLicenceAge: PropTypes.string,
    modified: PropTypes.string,
    factoryOptions: PropTypes.object,
    nonStandardAccessories: PropTypes.object,
    securityFeatures: PropTypes.string,
    damaged: PropTypes.string,
    finance: PropTypes.string,
    currentlyInsured: PropTypes.string,
    previousInsurer: PropTypes.string,
    annualKilometres: PropTypes.string,
    useType: PropTypes.string,
    driverGender: PropTypes.string,
    driverDob: PropTypes.string,
    driverEmployment: PropTypes.string,
    driverAge: PropTypes.string,
    driverLicenceType: PropTypes.string,
    comprehensiveInsured: PropTypes.string,
    comprehensiveInsuredYears: PropTypes.string,
    driverPreviousClaims: PropTypes.string,
    driverPreviousClaimsCount: PropTypes.number,
    driverOwnsAnotherCar: PropTypes.string,
    driverOwnsAnotherHome: PropTypes.string,
    driverOption: PropTypes.string,
    splitTrafficId: PropTypes.string,
    hasSavedResults: PropTypes.bool
};

const mapStateToProps = (state) => {
    return {
        hasSavedResults: getHasSavedResults(),
        env: state.getIn(['serverConfig', 'env'])
    };
};

export { _EditMyDetails };
export const EditMyDetails = connect(mapStateToProps)(_EditMyDetails);
